// The project settings page's section registry: each section's component, label, validator and edited-state
// fingerprint, in the order the page renders them.

// Vue
import { markRaw } from "vue";

// Features
import { BuildingProjectType, services as BuildingProjectServices } from "@/features/BuildingProject";

// Components
import Address from "@/features/BuildingProject/components/WizardFlow/Address/Address.vue";
import ProjectDetails from "@/features/BuildingProject/components/WizardFlow/ProjectDetails/ProjectDetails.vue";
import CalculationDetails from "@/features/BuildingProject/components/WizardFlow/ProjectDetails/CalculationDetails.vue";
import BuildingWithReferenceArea from "@/features/BuildingProject/components/WizardFlow/BuildingDetails/BuildingWithReferenceArea.vue";
import Transport from "@/features/BuildingProject/components/WizardFlow/Transport/Transport.vue";
import Installation from "@/features/BuildingProject/components/WizardFlow/Installation/Installation.vue";
import OperationalEnergyUse from "@/features/BuildingProject/components/WizardFlow/OperationalEnergyUse.vue";
import EditSpeckleConnection from "@/features/BuildingProject/components/WizardFlow/EditSpeckleConnection/EditSpeckleConnection.vue";
import Models from "@/features/BuildingProject/components/WizardFlow/Models/Models.vue";
import ClassificationSection from "./ClassificationSection.vue";
import { buildingAreaFingerprint } from "./buildingAreaFingerprint";
import { buildingUsesValid } from "@/features/BuildingProject/utils/buildingUsesValidity";
import Allowances from "@/features/BuildingProject/components/WizardFlow/Allowances/Allowances.vue";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import {
  allowancesApply,
  referenceAreaApplies,
  picksTransportSourceInMapping,
} from "@/features/BuildingProject/utils/wizardStepConditions";

export type SettingsSectionType = {
  // String name for step
  id: string;

  // Label for step
  label: string;

  // Component for step
  component: any;

  // Validation function for step; `saved` is the last-saved project, for rules that only apply to edited fields
  valid: (project: BuildingProjectType, saved?: BuildingProjectType | null) => boolean;

  /** Whether the reader has put anything in this step — `valid` alone can't tell "trivially passes" from "filled in", so only steps with a constant `true` validator define this. */
  hasData?: (project: BuildingProjectType) => boolean;

  /** The slice of the project this step edits, diffed (JSON.stringify, matching `payloadChanged`) against the last save to mark the section touched. Return fields, not a boolean — state stored outside the project belongs to `hasExternalEdits` instead. */
  fingerprint?: (project: BuildingProjectType) => unknown;

  // Options for step
  options: {
    // Show in top navigation with icons - true for most
    includeInStepOverview: boolean;

    // Include custom pagination - true for last steps and oddities
    includeCustomPagination?: boolean;

    // Condition to show step based on project data
    projectCondition?: (project: BuildingProjectType) => boolean;

    // The step stays in the flow (validated, saved, edits tracked) but isn't rendered as a settings
    // section — another surface of the page hosts it (e.g. the Speckle integration's own screen).
    excludeFromSettingsSections?: boolean;
  };
};

// Wrapped in a factory so component references resolve lazily (see the matching comment in
// createWizardSteps.ts) — this avoids a circular import through the @/features/BuildingProject barrel.
const settingsSections = (): SettingsSectionType[] => [
  {
    id: "PROJECT_DETAILS",
    label: "Project",
    component: markRaw(ProjectDetails),
    valid(project: BuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateProjectDetails(project);
    },
    /** Everything this step's two cards hold: the project's basics (name, status, years), and the optional details. */
    fingerprint(project: BuildingProjectType) {
      const assumptions = project?.emissionCalculationAssumptions;
      return {
        projectName: project?.projectName,
        projectNumber: project?.projectNumber,
        clientName: project?.clientName,
        builder: project?.builder,
        lcaResponsibleName: project?.lcaResponsibleName,
        numberOfPeople: project?.numberOfPeople,
        buildingProjectStageEnum: project?.buildingProjectStageEnum,
        yearOfConstruction: assumptions?.emissionMetaData?.yearOfConstruction,
        yearOfCommissioning: assumptions?.emissionMetaData?.yearOfCommissioning,
      };
    },
    options: {
      includeInStepOverview: true,
    },
  },
  {
    id: "LOCATION",
    label: "Location",
    component: markRaw(Address),
    /** Optional, deliberately: `runSave` refuses the whole save when any section is invalid, and every start-dialog project carries a country but no street with no edit surface demanding one. */
    valid() {
      return true;
    },
    /** A street is the claim — the country alone doesn't count, since it arrives from the start dialog on every project and would mark an untouched section complete. */
    hasData(project: BuildingProjectType) {
      const street = project?.buildingAddress?.street;
      return typeof street === "string" && street.length > 0;
    },
    /** A field belongs to exactly one fingerprint, or one edit marks two rail rows. */
    fingerprint(project: BuildingProjectType) {
      return { buildingAddress: project?.buildingAddress };
    },
    options: {
      includeInStepOverview: true,
    },
  },
  {
    id: "BUILDING_DETAILS",
    label: "Building area",
    component: markRaw(BuildingWithReferenceArea),
    /** Heated-over-gross is checked only on edited pairs (`validateHeatedAreasForEdit`), since stored data can already fail it. */
    valid(project: BuildingProjectType, saved?: BuildingProjectType | null) {
      const { validateBuildingDetails, validateHeatedAreasForEdit, validateReferenceArea } =
        BuildingProjectServices.validateBuildingProject;
      if (!validateBuildingDetails(project)) return false;
      if (!validateHeatedAreasForEdit(project, saved)) return false;
      if (!buildingUsesValid(project)) return false;
      return referenceAreaApplies(project) ? validateReferenceArea(project) : true;
    },
    /** Areas go through `buildingAreaFingerprint`, which compares the ROWS a save would write — raw fields would mark the section "edited" right after its own save, since NOT-NULL columns turn an unanswered field into a stored 0. */
    fingerprint(project: BuildingProjectType) {
      return {
        buildingTypeEnum: project?.buildingTypeEnum,
        areas: buildingAreaFingerprint(project),
        buildingUses: project?.buildingUses ?? [],
      };
    },
    options: {
      includeInStepOverview: true,
    },
  },
  {
    id: "CALCULATION",
    label: "Calculation",
    component: markRaw(CalculationDetails),
    valid(project: BuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateCalculationPrerequisites(project);
    },
    /** The calculation type and the chosen life-cycle stages, moved here from PROJECT_DETAILS' fingerprint. */
    fingerprint(project: BuildingProjectType) {
      const assumptions = project?.emissionCalculationAssumptions;
      const calculationType = assumptions?.calculationType;
      return {
        // `useDefaultEmissions` lives on this object but belongs to ALLOWANCES' fingerprint — a field
        // belongs to exactly one fingerprint, so it's stripped here.
        calculationType: calculationType && { ...calculationType, useDefaultEmissions: undefined },
        includedPhases: assumptions?.emissionMetaData?.includedPhases,
        includedPhasesTranslated: assumptions?.emissionMetaData?.includedPhasesTranslated,
      };
    },
    options: {
      includeInStepOverview: true,
    },
  },
  {
    id: "MODELS",
    label: "Models",
    component: markRaw(Models),
    /** Optional, like create's own Models section: a project works without naming any model, mapped by hand or from Excel instead. */
    valid() {
      return true;
    },
    /** Mirrors what ProjectWrapperProvider derives its model list from — not `modelNames`, which also unions in material branch names and would falsely mark this step as having data. */
    hasData(project: BuildingProjectType) {
      return (project?.speckleData?.branches?.length ?? 0) > 0 || (project?.speckleData?.inactiveBranches?.length ?? 0) > 0;
    },
    // No fingerprint: model state lives on the wrapper, not the project, so `hasExternalEdits("MODELS")`
    // is this section's whole edited signal.
    options: {
      includeInStepOverview: true,
    },
  },
  {
    id: "CLASSIFICATION",
    label: "Classification",
    component: markRaw(ClassificationSection),
    /** Optional in the wizard's sense — a project computes without it, but results are grouped by the classification code, so the rail marks it once a parameter is chosen. */
    valid() {
      return true;
    },
    // The system alone is a default the card fills in by itself; the parameter is the user's choice.
    hasData(project: BuildingProjectType) {
      const parameter = project?.speckleConfiguration?.parameters?.classificationCode;
      return typeof parameter === "string" && parameter.length > 0;
    },
    /** The one project field this section edits — a field belongs to exactly one fingerprint. */
    fingerprint(project: BuildingProjectType) {
      return { speckleConfiguration: project?.speckleConfiguration };
    },
    options: {
      includeInStepOverview: true,
    },
  },
  {
    id: "ALLOWANCES",
    label: "Standard values & special conditions",
    component: markRaw(Allowances),
    /** Nothing to validate, same as create's ALLOWANCES. */
    valid() {
      return true;
    },
    /** "Touched" is either master switch being on (standard values' `useDefaultEmissions`, or special conditions' own config row) — per-stage rows aren't checked separately since toggling one flips the master switch too. */
    hasData(project: BuildingProjectType) {
      return Boolean(
        project?.emissionCalculationAssumptions?.calculationType?.useDefaultEmissions || project?.specialConditionConfig?.enabled
      );
    },
    /** The two master switches only — per-stage rows live in a store, not the project, so `hasExternalEdits("ALLOWANCES")` answers for them and the settings page ORs the two together. */
    fingerprint(project: BuildingProjectType) {
      return {
        useDefaultEmissions: project?.emissionCalculationAssumptions?.calculationType?.useDefaultEmissions,
        // `enabled`, not the whole config row: the server's row carries `projectId` and the live one
        // doesn't, so comparing objects would mark this section edited on every arrival.
        specialConditionsEnabled: project?.specialConditionConfig?.enabled ?? false,
      };
    },
    options: {
      includeInStepOverview: true,
      /** Same predicate and lookup create's ALLOWANCES step uses, so the step shows under either wizard on the same terms. */
      projectCondition(project: BuildingProjectType) {
        const calculationTypes = useCalculationTypes();
        const calculationTypeEnum = project.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum;
        const calculationType =
          calculationTypeEnum === null || calculationTypeEnum === undefined
            ? undefined
            : calculationTypes.getSelectedCalculationType(calculationTypeEnum);

        return allowancesApply(project, calculationType);
      },
    },
  },
  {
    id: "TRANSPORT",
    label: "Transport (A4)",
    component: markRaw(Transport),
    valid(project: BuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateTransport(project, project.transportTypes);
    },
    // `validateTransport` only rejects malformed ROWS — no rows is not a failure, so a project that has
    // never been near this section passes it. Having any row at all is the claim.
    hasData(project: BuildingProjectType) {
      return (project?.transportTypes?.length ?? 0) > 0;
    },
    /** The rows themselves, which is all this step edits. */
    fingerprint(project: BuildingProjectType) {
      return project?.transportTypes;
    },
    options: {
      includeInStepOverview: true,
      projectCondition(project: BuildingProjectType) {
        if (picksTransportSourceInMapping(project)) return false;
        // Skip TRANSPORT step if calculationTypeEnum is 38 (Low emission) or 39 (Low emission 2025)
        const calculationTypeEnum = project?.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum;
        if (calculationTypeEnum === 38 || calculationTypeEnum === 39) {
          return false;
        }
        return project?.emissionCalculationAssumptions?.emissionMetaData?.includedPhasesTranslated?.includes("A4");
      },
    },
  },
  {
    id: "INSTALLATION",
    label: "Installation (A5)",
    component: markRaw(Installation),
    valid(project: BuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateInstallationEnergyUse(project);
    },
    /** Mirrors the validator's own notion of input (rows carrying a `sourceId`) so the two can't disagree about what counts as filled in. */
    hasData(project: BuildingProjectType) {
      const constructional = project?.emissionCalculationAssumptions?.projectEnergyData?.constructional;
      return Boolean(
        constructional?.earthMoved?.sourceId ||
          constructional?.electricity?.sourceId ||
          constructional?.heat?.sourceId ||
          constructional?.machineryFuel?.sourceId
      );
    },
    /** The four supply rows and wastage percentage only — machinery-fuel emission-factor rows live in InstallationEmissionFactorsProvider, so `hasExternalEdits("INSTALLATION")` answers for them instead. */
    fingerprint(project: BuildingProjectType) {
      const assumptions = project?.emissionCalculationAssumptions;
      return {
        constructional: assumptions?.projectEnergyData?.constructional,
        wastagePercentage: assumptions?.wastagePercentage,
      };
    },
    options: {
      includeInStepOverview: true,
      projectCondition(project: BuildingProjectType) {
        return project?.emissionCalculationAssumptions?.emissionMetaData?.includedPhasesTranslated?.includes("A5");
      },
    },
  },
  {
    id: "OPERATIONAL",
    label: "Energy use (B6)",
    component: markRaw(OperationalEnergyUse),
    valid(project: BuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateOperationalEnergyUse(project);
    },
    /** Mirrors `validateOperationalEnergyUse`'s own `hasUserInput` check, for the same reason as Installation above — the two must not disagree. */
    hasData(project: BuildingProjectType) {
      const energyData = project?.emissionCalculationAssumptions?.projectEnergyData;
      const exported = energyData?.operationExportedElectricity;
      return Boolean(
        energyData?.operational?.heat?.sourceId ||
          energyData?.operational?.electricity?.sourceId ||
          (typeof exported === "number" && exported > 0)
      );
    },
    /** The B6 rows and the exported electricity, which is the same state the validator reads. */
    fingerprint(project: BuildingProjectType) {
      const energyData = project?.emissionCalculationAssumptions?.projectEnergyData;
      return { operational: energyData?.operational, operationExportedElectricity: energyData?.operationExportedElectricity };
    },
    options: {
      includeInStepOverview: true,
      projectCondition(project: BuildingProjectType) {
        return project?.emissionCalculationAssumptions?.emissionMetaData?.includedPhasesTranslated?.includes("B6");
      },
    },
  },
  {
    id: "SPECKLE",
    // Labeled "3D model", not "Speckle" — the id stays SPECKLE since other code wires against that
    // string; the component stays EditSpeckleConnection, which handles streamId/branch, unlike create's.
    label: "3D model",
    component: markRaw(EditSpeckleConnection),
    valid() {
      return true;
    },
    // A model is attached or not — null and "" both mean "not": the start dialog's base insert writes
    // null, a failed attach leaves "". This keeps the rail unmarked until a connection actually exists.
    hasData(project: BuildingProjectType) {
      return typeof project?.streamId === "string" && project.streamId.length > 0;
    },
    /** The connection itself only — active models are the MODELS step's job (`hasExternalEdits("MODELS")`), and parameter mapping now belongs to CLASSIFICATION. */
    fingerprint(project: BuildingProjectType) {
      return { streamId: project?.streamId };
    },
    options: {
      includeInStepOverview: true,
      // Hosted by the Speckle integration's own screen, not listed among the sections — the step stays
      // in the flow so save still writes the active model list and the rail's guard still sees edits.
      excludeFromSettingsSections: true,
      // No projectCondition, deliberately: `streamId === null` now means "not yet connected", not
      // "predates Speckle" — hiding the step on null would remove the only connect affordance.
    },
  },
];

export default settingsSections;
